import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Autocomplete,
  Chip,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Controller } from 'react-hook-form';
import { useUpdateCourseForm } from '../../hooks/forms/moodle/useUpdateCourseForm';
import { useGetOfficeLocations } from '../../hooks/queries/masterdata/useGetOfficeLocations';

const RECURRENCE_OPTIONS = [
  { label: 'Yearly', value: 'YEARLY' },
  { label: 'Monthly', value: 'MONTHLY' },
  { label: 'Once', value: 'ONCE' },
];

const EditCourseModal = ({ open, onClose, course }) => {
  const { form, onSubmit, isPending } = useUpdateCourseForm(course, onClose);
  const { locations, loading: locationsLoading } = useGetOfficeLocations();
  const {
    register,
    control,
    watch,
    formState: { errors },
  } = form;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit Mandatory Course</DialogTitle>
      <form onSubmit={onSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Course Name"
                fullWidth
                disabled
                value={course?.courseNameSnapshot ?? ''}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Year"
                fullWidth
                disabled
                value={course?.year ?? ''}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Launch Date"
                fullWidth
                disabled
                InputLabelProps={{ shrink: true }}
                error={!!errors.launchDate}
                helperText={errors.launchDate?.message}
                {...register('launchDate')}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="End Date"
                fullWidth
                disabled
                InputLabelProps={{ shrink: true }}
                error={!!errors.endDate}
                helperText={errors.endDate?.message}
                {...register('endDate')}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Grace Days"
                type="number"
                fullWidth
                inputProps={{ min: 0 }}
                error={!!errors.graceDays}
                helperText={errors.graceDays?.message}
                {...register('graceDays')}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Freeze Days"
                type="number"
                fullWidth
                inputProps={{ min: 0 }}
                error={!!errors.freezeDays}
                helperText={errors.freezeDays?.message}
                {...register('freezeDays')}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.recurrence}>
                <InputLabel>Recurrence</InputLabel>
                <Controller
                  name="recurrence"
                  control={control}
                  render={({ field }) => (
                    <Select {...field} label="Recurrence">
                      {RECURRENCE_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                {errors.recurrence && (
                  <FormHelperText>{errors.recurrence.message}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="warningNotificationDays"
                control={control}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={field.value.map(String)}
                    disabled={
                      watch('graceDays') === 0 || watch('graceDays') === '0'
                    }
                    onChange={(e, newValue) => {
                      const nums = newValue
                        .map((v) => parseInt(v, 10))
                        .filter((n) => !isNaN(n));
                      field.onChange([...new Set(nums)]);
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={option}
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Warning Notification Days"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="completionCcEmails"
                control={control}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={field.value || []}
                    onChange={(e, newValue) => field.onChange(newValue)}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={option}
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Completion CC Emails"
                        error={!!errors?.completionCcEmails}
                        helperText={
                          errors?.completionCcEmails
                            ? 'Please enter valid email(s)'
                            : ''
                        }
                      />
                    )}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={12}>
              <Controller
                name="applicableLocationIds"
                control={control}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    multiple
                    filterSelectedOptions
                    options={locations}
                    loading={locationsLoading}
                    getOptionLabel={(option) => option.locationName || ''}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    value={(field.value || [])
                      .map((id) => locations.find((loc) => loc.id === id))
                      .filter(Boolean)}
                    onChange={(e, newValue) => {
                      field.onChange(newValue.map((loc) => loc.id));
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={option.locationName}
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Applicable Locations"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="isMandatory"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={!!field.value} />}
                    label="Mandatory"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={!!field.value} />}
                    label="Active"
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <LoadingButton onClick={onClose} disabled={isPending}>
            Cancel
          </LoadingButton>
          <LoadingButton type="submit" variant="contained" loading={isPending}>
            Save Changes
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditCourseModal;
